import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { clearCart } from "@/lib/cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearCart();
          setState("paid");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#F3EDE3] text-[#0A0A0A] flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg w-full bg-white border border-[#DCD3C3] p-10 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#B38728]" />
            <h1 className="font-serif text-3xl mt-6">Confirming your payment…</h1>
            <p className="text-[#5C564C] mt-3 text-sm">Hold tight while we verify with our payment partner.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#B38728]" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#B38728] mt-6">Order Confirmed</p>
            <h1 className="font-serif text-4xl mt-3" data-testid="payment-success-status">Welcome to the vault</h1>
            <p className="text-[#5C564C] mt-4 text-sm leading-relaxed">
              Your collectible is secured. We'll email you shortly to arrange insured shipping or local pickup.
            </p>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <h1 className="font-serif text-3xl" data-testid="payment-success-status">Almost there</h1>
            <p className="text-[#5C564C] mt-4 text-sm leading-relaxed">
              We're still confirming your payment. If you were charged, your order is safe — contact us at
              Officialstudexchange@hotmail.com and we'll sort it immediately.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="inline-block mt-8 bg-[#0A0A0A] text-[#F3EDE3] px-8 py-3 text-xs font-mono uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors"
        >
          Back to the Vault
        </Link>
      </motion.div>
    </main>
  );
}
